import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart3, Calendar, Download, Printer, TrendingUp, ShoppingBag, CreditCard, Banknote, Filter, ChefHat, Users, Clock, Award, Star } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { EndOfDayButton } from './EndOfDayButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_STYLE = { diger: 'border-purple-400', paket: 'border-orange-400', online: 'border-sky-400', veresiye: 'border-rose-400' };
const GROUP_LABEL = { diger: 'Salon/Masa', paket: 'Paket', online: 'Online', veresiye: 'Veresiye', iptal: 'İptal' };
const typeLabel = (p) => p.item_type === 'catering' ? `Günlük Menü${p.category ? ' · ' + p.category : ''}` : p.item_type === 'beverage' ? 'İçecek' : 'A la carte';
const productRowsHtml = (list) => list.map((p, i) => `<tr><td>${i+1}</td><td>${p.name}</td><td>${typeLabel(p)}</td><td class="num">${p.quantity}</td><td class="num">€${p.revenue.toFixed(2)}</td></tr>`).join('')
  + `<tr class="total-row"><td colspan="3">TOPLAM</td><td class="num">${list.reduce((s, p) => s + p.quantity, 0)}</td><td class="num">€${list.reduce((s, p) => s + p.revenue, 0).toFixed(2)}</td></tr>`;
const PRODUCT_HEAD = '<table><thead><tr><th>#</th><th>Ürün</th><th>Tür</th><th style="text-align:right">Adet</th><th style="text-align:right">Ciro</th></tr></thead><tbody>';

const SalesReport = ({ token }) => {
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [groupFilter, setGroupFilter] = useState('all');
  const reportRef = useRef(null);
  const selectedGroup = report?.sales_groups?.find(g => g.key === groupFilter);
  const shownProducts = (selectedGroup ? selectedGroup.products : report?.products) || [];

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports/sales?start_date=${startDate}&end_date=${endDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(res.data);
    } catch (e) {
      toast.error('Rapor yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, token]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const setQuickDate = (type) => {
    const today = new Date();
    const fmt = d => format(d, 'yyyy-MM-dd');
    if (type === 'today') { setStartDate(fmt(today)); setEndDate(fmt(today)); }
    else if (type === 'yesterday') { const y = new Date(today); y.setDate(y.getDate()-1); setStartDate(fmt(y)); setEndDate(fmt(y)); }
    else if (type === 'week') { const w = new Date(today); w.setDate(w.getDate()-7); setStartDate(fmt(w)); setEndDate(fmt(today)); }
    else if (type === 'month') { const m = new Date(today); m.setDate(1); setStartDate(fmt(m)); setEndDate(fmt(today)); }
  };

  const printReport = () => {
    const content = reportRef.current;
    if (!content) return;
    const printHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Satış Raporu ${startDate} - ${endDate}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;padding:20px;font-size:14px}
    h1{font-size:22px;margin-bottom:5px}h2{font-size:16px;margin:20px 0 10px;border-bottom:2px solid #000;padding-bottom:5px}
    .summary{display:flex;gap:20px;margin:15px 0}.summary-card{flex:1;border:2px solid #333;padding:12px;text-align:center}
    .summary-card .val{font-size:24px;font-weight:900}.summary-card .lbl{font-size:11px;color:#666}
    table{width:100%;border-collapse:collapse;margin:10px 0}th,td{border:1px solid #ccc;padding:8px;text-align:left}
    th{background:#f0f0f0;font-weight:700}td.num{text-align:right;font-weight:600}.total-row{font-weight:900;background:#f5f5f5}
    @media print{body{padding:10px}}</style></head><body>
    <h1>CENTRAL RESTAURANT - SATIŞ RAPORU</h1>
    <p>${startDate === endDate ? startDate : `${startDate} - ${endDate}`} | ${report?.order_count || 0} sipariş</p>
    <div class="summary">
      <div class="summary-card"><div class="val">€${report?.total_revenue?.toFixed(2) || '0.00'}</div><div class="lbl">TOPLAM CİRO</div></div>
      <div class="summary-card"><div class="val">${report?.order_count || 0}</div><div class="lbl">SİPARİŞ</div></div>
      <div class="summary-card"><div class="val">€${report?.order_count ? (report.total_revenue / report.order_count).toFixed(2) : '0.00'}</div><div class="lbl">ORT. SİPARİŞ</div></div>
    </div>
    <h2>SATIŞ GRUPLARI</h2>
    <table><thead><tr><th>Grup</th><th style="text-align:right">Satış</th><th style="text-align:right">Ciro</th></tr></thead><tbody>
    ${(report?.sales_groups || []).map(g => `<tr><td>${g.label}</td><td class="num">${g.count}</td><td class="num">€${g.revenue.toFixed(2)}</td></tr>`).join('')}
    <tr><td>İptal (ciroya dahil değil)</td><td class="num">${report?.cancelled?.count || 0}</td><td class="num">€${report?.cancelled?.amount?.toFixed(2) || '0.00'}</td></tr>
    </tbody></table>
    <h2>ÜRÜN BAZLI SATIŞ (TÜMÜ)</h2>
    ${PRODUCT_HEAD}${productRowsHtml(report?.products || [])}</tbody></table>
    ${(report?.sales_groups || []).filter(g => g.products.length).map(g => `<h2>${g.label.toUpperCase()} - ÜRÜNLER</h2>${PRODUCT_HEAD}${productRowsHtml(g.products)}</tbody></table>`).join('')}
    <h2>ÖDEME DAĞILIMI</h2>
    <table><thead><tr><th>Yöntem</th><th style="text-align:right">Adet</th></tr></thead><tbody>
    ${Object.entries(report?.payment_breakdown || {}).map(([k, v]) => `<tr><td>${k === 'nakit' ? 'Nakit' : k === 'kart' ? 'Kredi Kartı' : k}</td><td class="num">${v}</td></tr>`).join('') || ''}
    </tbody></table>
    ${report?.catering_details?.length ? `
    <h2>CATERİNG YEMEK DETAYI</h2>
    <table><thead><tr><th>#</th><th>Yemek</th><th>Kategori</th><th style="text-align:right">Adet</th><th style="text-align:right">Ciro</th></tr></thead><tbody>
    ${report.catering_details.map((d, i) => `<tr><td>${i+1}</td><td>${d.name}</td><td>${d.category}</td><td class="num">${d.quantity}</td><td class="num">${d.revenue ? '€' + d.revenue.toFixed(2) : '-'}</td></tr>`).join('')}
    </tbody></table>` : ''}
    </body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open(); doc.write(printHtml); doc.close();
    setTimeout(() => {
      iframe.contentWindow.focus(); iframe.contentWindow.print();
      setTimeout(() => { try { document.body.removeChild(iframe); } catch(e) {} }, 3000);
    }, 300);
  };

  return (
    <div className="space-y-6" ref={reportRef}>
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-basil/10 rounded-full flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-basil" />
          </div>
          <div>
            <h2 className="font-playfair text-xl font-semibold text-charcoal">Satış Raporu</h2>
            <p className="text-sm text-charcoal/60">Günlük ve dönemsel satış analizi</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setQuickDate('today')} data-testid="filter-today">Bugün</Button>
            <Button size="sm" variant="outline" onClick={() => setQuickDate('yesterday')} data-testid="filter-yesterday">Dün</Button>
            <Button size="sm" variant="outline" onClick={() => setQuickDate('week')} data-testid="filter-week">Son 7 Gün</Button>
            <Button size="sm" variant="outline" onClick={() => setQuickDate('month')} data-testid="filter-month">Bu Ay</Button>
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" data-testid="start-date" />
            <span className="text-charcoal/40">-</span>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" data-testid="end-date" />
          </div>
          <Button onClick={fetchReport} disabled={loading} className="bg-basil text-white hover:bg-basil/90" data-testid="fetch-report-btn">
            <Filter className="w-4 h-4 mr-2" />{loading ? 'Yükleniyor...' : 'Filtrele'}
          </Button>
          {report && (
            <Button onClick={printReport} variant="outline" data-testid="print-report-btn">
              <Printer className="w-4 h-4 mr-2" />Çıktı Al
            </Button>
          )}
          <EndOfDayButton token={token} date={endDate} />
        </div>
      </div>

      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="total-revenue-card">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-basil" />
                <span className="text-sm text-charcoal/60">Toplam Ciro</span>
              </div>
              <p className="font-playfair text-2xl font-bold text-basil">€{report.total_revenue?.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="order-count-card">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingBag className="w-5 h-5 text-terracotta" />
                <span className="text-sm text-charcoal/60">Sipariş Sayısı</span>
              </div>
              <p className="font-playfair text-2xl font-bold text-terracotta">{report.order_count}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-amber-600" />
                <span className="text-sm text-charcoal/60">Ortalama Sipariş</span>
              </div>
              <p className="font-playfair text-2xl font-bold text-amber-600">€{report.order_count ? (report.total_revenue / report.order_count).toFixed(2) : '0.00'}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-charcoal/60">Ödeme</span>
              </div>
              <div className="flex gap-3 text-sm">
                <span className="flex items-center gap-1"><Banknote className="w-4 h-4 text-green-600" />{report.payment_breakdown?.nakit || 0}</span>
                <span className="flex items-center gap-1"><CreditCard className="w-4 h-4 text-blue-600" />{report.payment_breakdown?.kart || 0}</span>
              </div>
            </div>
          </div>

          {/* Sales Groups */}
          {report.sales_groups && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-2" data-testid="sales-groups">
              {report.sales_groups.map(g => (
                <button key={g.key} type="button" onClick={() => setGroupFilter(groupFilter === g.key ? 'all' : g.key)}
                  className={`text-left bg-white rounded-xl p-4 shadow-sm border-l-4 transition-shadow hover:shadow-md ${GROUP_STYLE[g.key]} ${groupFilter === g.key ? 'ring-2 ring-offset-1 ring-charcoal/30' : ''}`}
                  data-testid={`sales-group-${g.key}`}>
                  <p className="text-sm text-charcoal/60 mb-1">{g.label}</p>
                  <p className="font-bold text-lg">{'\u20AC'}{g.revenue.toFixed(2)}</p>
                  <p className="text-xs text-charcoal/40">{g.count} satış</p>
                </button>
              ))}
              <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-red-300" data-testid="sales-group-cancelled">
                <p className="text-sm text-charcoal/60 mb-1">İptal (ciroya dahil değil)</p>
                <p className="font-bold text-lg text-red-500 line-through">{'\u20AC'}{report.cancelled?.amount?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-charcoal/40">{report.cancelled?.count || 0} sipariş</p>
              </div>
            </div>
          )}

          {/* Top 5 Sellers */}
          {report.top_sellers?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="top-sellers-section">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-charcoal">En Cok Satan 5 Urun</h3>
              </div>
              <div className="space-y-3">
                {report.top_sellers.map((p, i) => {
                  const maxRevenue = report.top_sellers[0]?.revenue || 1;
                  const pct = (p.revenue / maxRevenue) * 100;
                  return (
                    <div key={`top-${p.name}`} className="flex items-center gap-3" data-testid={`top-seller-${i}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-stone-200 text-stone-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-stone-100 text-stone-500'}`}>
                        {i === 0 ? <Star className="w-4 h-4" /> : i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-charcoal truncate">{p.name}</span>
                          <span className="font-bold text-terracotta ml-2">{'\u20AC'}{p.revenue.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 bg-stone-100 rounded-full h-2 overflow-hidden">
                            <div className="h-full bg-terracotta/70 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-charcoal/50 min-w-[40px] text-right">{p.quantity} adet</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Waiter Performance */}
          {report.waiter_performance?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="waiter-performance-section">
              <div className="p-4 border-b bg-indigo-50">
                <h3 className="font-semibold text-charcoal flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Garson Performansi
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="waiter-table">
                  <thead>
                    <tr className="border-b bg-stone-50/50">
                      <th className="text-left p-3 text-sm font-medium text-charcoal/70">#</th>
                      <th className="text-left p-3 text-sm font-medium text-charcoal/70">Garson</th>
                      <th className="text-right p-3 text-sm font-medium text-charcoal/70">Siparis</th>
                      <th className="text-right p-3 text-sm font-medium text-charcoal/70">Ciro</th>
                      <th className="text-right p-3 text-sm font-medium text-charcoal/70">Ort. Siparis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.waiter_performance.map((w, i) => (
                      <tr key={`waiter-${w.name}`} className="border-b last:border-b-0 hover:bg-indigo-50/30">
                        <td className="p-3 text-sm text-charcoal/60">{i + 1}</td>
                        <td className="p-3 font-medium text-charcoal flex items-center gap-2">
                          {i === 0 && <Star className="w-4 h-4 text-amber-500" />}
                          {w.name}
                        </td>
                        <td className="p-3 text-right font-semibold">{w.order_count}</td>
                        <td className="p-3 text-right font-bold text-terracotta">{'\u20AC'}{w.revenue.toFixed(2)}</td>
                        <td className="p-3 text-right font-semibold text-charcoal/70">{'\u20AC'}{w.avg_order.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-indigo-50 font-bold">
                      <td colSpan={2} className="p-3">TOPLAM</td>
                      <td className="p-3 text-right">{report.waiter_performance.reduce((s, w) => s + w.order_count, 0)}</td>
                      <td className="p-3 text-right text-terracotta">{'\u20AC'}{report.waiter_performance.reduce((s, w) => s + w.revenue, 0).toFixed(2)}</td>
                      <td className="p-3 text-right text-charcoal/70">-</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Hourly Distribution */}
          {report.hourly_distribution?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="hourly-section">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-charcoal">Saatlik Dagılım</h3>
              </div>
              <div className="flex items-end gap-1 h-40">
                {report.hourly_distribution.map((h, i) => {
                  const maxCount = Math.max(...report.hourly_distribution.map(x => x.count));
                  const pct = maxCount > 0 ? (h.count / maxCount) * 100 : 0;
                  return (
                    <div key={`hour-${h.hour}`} className="flex-1 flex flex-col items-center justify-end" data-testid={`hour-bar-${h.hour}`}>
                      <span className="text-xs font-bold text-charcoal mb-1">{h.count}</span>
                      <div className="w-full bg-green-500/70 rounded-t-sm transition-all" style={{ height: `${Math.max(pct, 4)}%` }} />
                      <span className="text-[10px] text-charcoal/50 mt-1">{h.hour}:00</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex gap-4 text-xs text-charcoal/50">
                <span>En yogun: <b className="text-charcoal">{report.hourly_distribution.reduce((a, b) => a.count > b.count ? a : b)?.hour}:00</b></span>
                <span>Toplam: <b className="text-charcoal">{report.hourly_distribution.reduce((s, h) => s + h.count, 0)} siparis</b></span>
              </div>
            </div>
          )}

          {/* Product Sales Table */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-stone-50 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-semibold text-charcoal">Ürün Bazlı Satış{selectedGroup ? ` · ${selectedGroup.label}` : ''}</h3>
              <div className="flex flex-wrap gap-1" data-testid="product-group-tabs">
                {[{ key: 'all', label: 'Tümü' }, ...(report.sales_groups || [])].map(g => (
                  <button key={g.key} type="button" onClick={() => setGroupFilter(g.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${groupFilter === g.key ? 'bg-charcoal text-white' : 'bg-white border text-charcoal/70 hover:bg-stone-100'}`}
                    data-testid={`product-group-tab-${g.key}`}>{g.label}</button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="product-sales-table">
                <thead>
                  <tr className="border-b bg-stone-50/50">
                    <th className="text-left p-3 text-sm font-medium text-charcoal/70">#</th>
                    <th className="text-left p-3 text-sm font-medium text-charcoal/70">Ürün</th>
                    <th className="text-left p-3 text-sm font-medium text-charcoal/70">Tür</th>
                    <th className="text-right p-3 text-sm font-medium text-charcoal/70">Adet</th>
                    <th className="text-right p-3 text-sm font-medium text-charcoal/70">Ciro</th>
                  </tr>
                </thead>
                <tbody>
                  {shownProducts.map((p, i) => (
                    <tr key={`prod-${p.name}`} className="border-b last:border-b-0 hover:bg-sand/30">
                      <td className="p-3 text-sm text-charcoal/60">{i + 1}</td>
                      <td className="p-3 font-medium text-charcoal">{p.name}</td>
                      <td className="p-3 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${p.item_type === 'catering' ? 'bg-amber-100 text-amber-700' : 'bg-basil/10 text-basil'}`}>
                          {p.item_type === 'catering' ? `Günlük Menü${p.category ? ` · ${p.category}` : ''}` : p.item_type === 'beverage' ? 'İçecek' : 'A la carte'}
                        </span>
                      </td>
                      <td className="p-3 text-right font-semibold">{p.quantity}</td>
                      <td className="p-3 text-right font-bold text-terracotta">€{p.revenue.toFixed(2)}</td>
                    </tr>
                  ))}
                  {shownProducts.length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center text-charcoal/40">Bu tarih aralığında satış bulunamadı</td></tr>
                  )}
                </tbody>
                {shownProducts.length > 0 && (
                  <tfoot>
                    <tr className="bg-stone-50 font-bold">
                      <td colSpan={3} className="p-3">TOPLAM</td>
                      <td className="p-3 text-right">{shownProducts.reduce((s, p) => s + p.quantity, 0)}</td>
                      <td className="p-3 text-right text-terracotta" data-testid="product-table-total">€{shownProducts.reduce((s, p) => s + p.revenue, 0).toFixed(2)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Catering Detail Sales */}
          {report.catering_details?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b bg-amber-50">
                <h3 className="font-semibold text-charcoal flex items-center gap-2">
                  <ChefHat className="w-5 h-5 text-amber-600" />
                  Catering Yemek Detayi
                </h3>
                <p className="text-xs text-charcoal/50 mt-1">Catering menülerinden siparis edilen her yemek ayri ayri</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="catering-detail-table">
                  <thead>
                    <tr className="border-b bg-stone-50/50">
                      <th className="text-left p-3 text-sm font-medium text-charcoal/70">#</th>
                      <th className="text-left p-3 text-sm font-medium text-charcoal/70">Yemek</th>
                      <th className="text-left p-3 text-sm font-medium text-charcoal/70">Kategori</th>
                      <th className="text-right p-3 text-sm font-medium text-charcoal/70">Toplam Adet</th>
                      <th className="text-right p-3 text-sm font-medium text-charcoal/70">Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.catering_details.map((d, i) => (
                      <tr key={`cat-${d.name}`} className="border-b last:border-b-0 hover:bg-amber-50/30">
                        <td className="p-3 text-sm text-charcoal/60">{i + 1}</td>
                        <td className="p-3 font-medium text-charcoal">{d.name}</td>
                        <td className="p-3 text-sm">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">{d.category}</span>
                        </td>
                        <td className="p-3 text-right font-bold">{d.quantity}</td>
                        <td className="p-3 text-right font-bold text-terracotta">{d.revenue ? `€${d.revenue.toFixed(2)}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-amber-50 font-bold">
                      <td colSpan={3} className="p-3">TOPLAM</td>
                      <td className="p-3 text-right">{report.catering_details.reduce((s, d) => s + d.quantity, 0)}</td>
                      <td className="p-3 text-right text-terracotta">€{report.catering_details.reduce((s, d) => s + (d.revenue || 0), 0).toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Orders List (Collapsible) */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <button
              onClick={() => setShowOrders(!showOrders)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-stone-50 transition-colors"
              data-testid="toggle-orders-list"
            >
              <h3 className="font-semibold text-charcoal">Sipariş Listesi ({report.orders?.filter(o => groupFilter === 'all' || o.sales_group === groupFilter).length || 0})</h3>
              <span className="text-charcoal/40 text-sm">{showOrders ? 'Gizle' : 'Göster'}</span>
            </button>
            {showOrders && (
              <div className="border-t">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-stone-50/50">
                      <th className="text-left p-3 text-xs font-medium text-charcoal/70">Sipariş</th>
                      <th className="text-left p-3 text-xs font-medium text-charcoal/70">Müşteri</th>
                      <th className="text-left p-3 text-xs font-medium text-charcoal/70">Tarih</th>
                      <th className="text-left p-3 text-xs font-medium text-charcoal/70">Grup</th>
                      <th className="text-left p-3 text-xs font-medium text-charcoal/70">Durum</th>
                      <th className="text-left p-3 text-xs font-medium text-charcoal/70">Ödeme</th>
                      <th className="text-right p-3 text-xs font-medium text-charcoal/70">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.orders?.filter(o => groupFilter === 'all' || o.sales_group === groupFilter).map(o => (
                      <tr key={o.id} className={`border-b last:border-b-0 hover:bg-sand/20 text-sm ${o.status === 'iptal' ? 'opacity-50 line-through' : ''}`} data-testid={`report-order-${o.id}`}>
                        <td className="p-3 font-mono font-bold">#{o.id?.slice(-8).toUpperCase()}</td>
                        <td className="p-3">{o.user_name}</td>
                        <td className="p-3 text-charcoal/60">{o.created_at ? format(new Date(o.created_at), 'd MMM HH:mm', { locale: tr }) : '-'}</td>
                        <td className="p-3 text-xs">{GROUP_LABEL[o.sales_group] || '-'}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${o.status === 'teslim_edildi' ? 'bg-green-100 text-green-700' : o.status === 'yolda' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {o.status === 'teslim_edildi' ? 'Teslim' : o.status === 'yolda' ? 'Yolda' : o.status === 'iptal' ? 'İptal' : o.status === 'veresiye' || o.status === 'veresiye_aktarildi' ? 'Veresiye' : 'Hazırl.'}
                          </span>
                        </td>
                        <td className="p-3">{o.payment_method === 'nakit' ? 'Nakit' : o.payment_method === 'veresiye' ? 'Veresiye' : 'Kart'}</td>
                        <td className="p-3 text-right font-bold">€{o.total_price?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SalesReport;
